import { AxiosResponse } from "axios";
import axiosClient from "./axios-client"; // Import the axiosClient instance
import {
  AddFavourites,
  LoadRequestHistoryParams,
  LoadRequestHistoryResponse,
  MyRequestItem,
  MyRequestParams,
  MyRequestResponse,
  FavouriteResponse,
  Favourites,
} from "@pages/YourRequest/types";

// Load Request API Endpoint
const GET_USER_LOAD_REQUESTS_URL = "/LoadRequest/UserRequests";
// API Endpoint
const GET_REQUEST_HISTORY_URL = "/LoadRequest/GetRequestHistory";

const GET_RECENT_REQUESTS_URL = "/LoadRequest/RecentRequests";

const GET_REQUEST_DETAILS_URL = "/LoadRequest/GetRequestDetailsByID";

const GET_REQUEST_UPDATES_URL = "/LoadRequest/RequestUpdates";

const LOAD_DELETE_REQUEST_URL = "/LoadRequest/DeleteRequest";

const LOAD_STATUS_UPDATE_URL = "/LoadRequest/EnableUpdate";

const LOAD_Report_InaccurateLoad_URL = "/LoadRequest/ReportInaccurateLoad";

const PIN_UNPIN_REQUESTS_URL = "/LoadRequest/PinUnpinRequests";

const CHECK_USER_AIRLINES_URL = "/LoadRequest/CheckUserAirlines";

const ADD_FAVOURITES = "/LoadRequest/AddFavourites";

const DELETE_FAVOURITE = "/LoadRequest/DeleteFavourite";

const GET_FAVOURITES = "/LoadRequest/GetFavourites";

const VALIDATE_FAVOURITE_FLIGHT = "/LoadRequest/ValidateFavouriteFlight";

/**
 * Fetch the user's load requests with pagination and sorting.
 * @param params Pagination and sorting parameters for load requests.
 * @returns The user's load request data.
 */
export const getUserLoadRequests = async (params: MyRequestParams): Promise<MyRequestResponse> => {
  try {
    const response: AxiosResponse<MyRequestResponse> = await axiosClient.get(GET_USER_LOAD_REQUESTS_URL, { params });

    return response as unknown as MyRequestResponse; // Ensure we return the array of load requests
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching user load requests:", error.message);
    } else {
      console.error("Unknown error fetching user load requests");
    }
    throw error;
  }
};

export const validateFavouriteFlight = async (favouriteId: number, targetDate: string): Promise<MyRequestResponse> => {
  try {
    const response = await axiosClient.post<MyRequestResponse>(VALIDATE_FAVOURITE_FLIGHT, {
      favouriteId,
      targetDate,
    });

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error validating favourite flight:", error.message);
    } else {
      console.error("Unknown error validating favourite flight");
    }
    throw error;
  }
};

export const getRecentRequests = async (params: MyRequestParams): Promise<MyRequestResponse> => {
  try {
    const response: AxiosResponse<MyRequestResponse> = await axiosClient.get(GET_RECENT_REQUESTS_URL, { params });

    return response as unknown as MyRequestResponse; // Ensure we return the array of load requests
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching user load requests:", error.message);
    } else {
      console.error("Unknown error fetching user load requests");
    }
    throw error;
  }
};

export const getLoadRequestHistory = async (
  params?: LoadRequestHistoryParams // Make params optional
): Promise<LoadRequestHistoryResponse> => {
  try {
    const response: AxiosResponse<LoadRequestHistoryResponse> = await axiosClient.get(
      GET_REQUEST_HISTORY_URL,
      { params: params || {} } // Use an empty object if params is not provided
    );
    return response as unknown as LoadRequestHistoryResponse;
  } catch (error) {
    console.error("Error fetching load request history:", error);
    throw error;
  }
};

export const getLoadRequestDetailsByID = async (requestDetailId: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_REQUEST_DETAILS_URL, {
      params: { requestDetailId },
    });

    return response;
  } catch (error) {
    console.error("Error fetching load request details:", error);
    throw error;
  }
};

export const getRequestUpdates = async (requestIds: number[]): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(
      GET_REQUEST_UPDATES_URL,
      { requestIds } // Sending requestIds in the request body
    );

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error fetching request updates:", error);
    throw error;
  }
};

export const deleteLoadRequests = async (requestIds: number[]): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(
      LOAD_DELETE_REQUEST_URL,
      requestIds // Passing the request IDs array directly
    );

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error deleting load requests:", error);
    throw error;
  }
};

// export const enableUpdateRequests = async (requestIds: number[]): Promise<any> => {
//   try {
//     const response: AxiosResponse<any> = await axiosClient.post(
//       LOAD_STATUS_UPDATE_URL,
//       requestIds // Passing the request IDs array directly
//     );

//     return response; // Returning the full response instead of response.data
//   } catch (error) {
//     console.error('Error updating load requests:', error);
//     throw error;
//   }
// };

export const enableUpdateRequests = async (requestIds: number[]): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(
      LOAD_STATUS_UPDATE_URL,
      requestIds // Sending array as request body
    );

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error updating load request:", error);
    throw error;
  }
};

export const reportInaccurateLoad = async (loadResponseId: number, comment: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(LOAD_Report_InaccurateLoad_URL, {
      loadResponseId,
      comment,
    });

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error reporting inaccurate load:", error);
    throw error;
  }
};

export const pinUnpinRequests = async (requestIds: number[], isPinned: boolean): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.put(
      `${PIN_UNPIN_REQUESTS_URL}?isPinned=${isPinned}`,
      requestIds, // Sending the request IDs as the request body
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error pinning/unpinning requests:", error);
    throw error;
  }
};

export const checkUserAirlines = async (airlineId: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(CHECK_USER_AIRLINES_URL, {
      params: { airlineId }, // Sending airlineId as a query parameter
    });

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error checking user airlines:", error);
    throw error;
  }
};

export const addFavourites = async (favourites: AddFavourites): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(ADD_FAVOURITES, favourites);

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error checking user airlines:", error);
    throw error;
  }
};

export const deleteFavourites = async (favouriteId?: number | null): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.delete(DELETE_FAVOURITE, {
      params: {
        favouriteId,
      },
    });

    return response; // Returning the full response instead of response.data
  } catch (error) {
    console.error("Error checking user airlines:", error);
    throw error;
  }
};

export const getFavourites = async (params: { page: number; pageSize: number }): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_FAVOURITES, {
      params, 
    });

    return response;
  } catch (error) {
    console.error("Error fetching favourites:", error);
    throw error;
  }
};
